/**
 * What a marketer has earned, what has been paid to them, and what is still owed.
 *
 * A marketer sells for the agency and does not work for it. ADR-0115 drew that line where ADR-0102
 * drew the buyer's: an outsider on commission **never holds a membership**, because a freelance
 * inside the agency's permission system would mean every screen in the product has to be safe
 * against a hostile outsider rather than against a colleague. So the only door they have is the
 * portal's, and until this screen existed they came through it and were shown nothing at all.
 *
 * **What is deliberately not here, because it is not in the answers either.** Nothing about a
 * second marketer — not their name, not their sales, not a count of them and not a hint that the
 * agency has one. No figure that belongs to the agency: not what a scheme has taken, not what is
 * left in it, not what anything else in it sold for. No internal id, because a purchase is named by
 * its reference and a reference is what somebody quotes on the phone. The API refuses all of it,
 * and a screen that implied any of it existed would be worse than one that showed it — a heading
 * over an empty panel is a promise there is more to see.
 *
 * **Money arrives already written out.** Every figure comes with a `…Label` beside its integer
 * count of minor units, formatted by the server in the agency's own currency. This page prints the
 * label and never does arithmetic on the number, so a marketer and the office cannot be reading two
 * different totals — which is the argument nobody can settle from the record.
 *
 * **Reached because `/portal/me` said so.** The account screen offers the link on `me.marketer`,
 * read from the `marketers` table rather than from `parties.roles`. Somebody who arrives here
 * anyway — an old bookmark, a link forwarded, a row deleted this morning — gets the same 404 the
 * endpoint gives, drawn as a wrong turn rather than as a refusal.
 */

import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { COMMISSION_BASIS_INFO, formatDate, formatRate, isCommissionBasis } from "@rentorng/core";
import { Alert, Badge, Button, Card, CardTitle, Detail, EmptyState, Spinner, Table } from "@/components/ui";
import { IconPayout, IconStatement } from "@/components/icons";
import { AgencyMark } from "@/components/agency-mark";
import { usePageTitle } from "@/lib/title";
import { PortalProvider, usePortal, type PortalMe } from "@/lib/portal";
import { PortalShell } from "./PortalShell";

/* ------------------------------------------------------------------ the shapes */

/** One accrual: the day a share of something collected became theirs. */
interface CommissionLine {
  reference: string;
  buyerName: string | null;
  estateName: string | null;
  plotLabel: string | null;
  date: string;
  description: string | null;
  gross: number;
  grossLabel: string;
  bp: number;
  commission: number;
  commissionLabel: string;
}

/** One payment out. A reversed one is not among them: a withdrawal shows as money coming back into
    what is owed rather than as a line to argue about. */
interface CommissionPayment {
  reference: string | null;
  paidOn: string;
  amount: number;
  amountLabel: string;
}

/** One sale they are on, whether or not it has earned anything yet. */
interface CommissionSale {
  reference: string;
  buyerName: string | null;
  estateName: string | null;
  plotLabel: string | null;
  status: string;
  commissionBp: number | null;
  basis: string | null;
  full: number;
  fullLabel: string;
  earned: number;
  earnedLabel: string;
}

interface CommissionAccount {
  marketer: { name: string; status: string };
  summary: {
    earned: number;
    earnedLabel: string;
    paid: number;
    paidLabel: string;
    owed: number;
    owedLabel: string;
    saleCount: number;
    lineCount: number;
  };
  lines: CommissionLine[];
  payments: CommissionPayment[];
  sales: CommissionSale[];
  asAt: string;
  /** Only on the statement, and only ever this agency's own contact details. */
  agency?: { name: string; phone: string | null; email: string | null; logoUrl?: string | null } | null;
}

/* --------------------------------------------------------------- small pieces */

/**
 * When a sale earns its commission, in the same words the agency's own screens use.
 *
 * Read out of `COMMISSION_BASIS_INFO` rather than written again here. A marketer told "As the buyer
 * pays" while the office reads something else about the same sale is precisely the disagreement
 * this feature exists to prevent, and a second copy of four labels is how that starts. A basis this
 * build has never heard of falls back to its own word rather than to a guess.
 */
function basisWords(basis: string | null): string {
  if (isCommissionBasis(basis)) return COMMISSION_BASIS_INFO[basis].label;
  return basis ? basis.replace(/_/g, " ") : "—";
}

/** How a sale is going, in a word rather than in a column of raw states. */
function SaleState({ status }: { status: string }) {
  if (status === "completed" || status === "settled") return <Badge tone="success">Settled</Badge>;
  if (status === "cancelled") return <Badge tone="danger">Cancelled</Badge>;
  if (status === "reserved") return <Badge tone="info">Reserved</Badge>;
  return <Badge tone="muted">Running</Badge>;
}

/** Which sale a row is about, in the words a marketer would use on the phone about it. */
function saleWords(row: { estateName: string | null; plotLabel: string | null; reference: string }): string {
  return [row.estateName, row.plotLabel].filter(Boolean).join(" · ") || row.reference;
}

/**
 * The sales they are on.
 *
 * Every one of them, including a sale that has earned nothing yet — a sale on a basis that has not
 * come round is worth showing as nothing, whereas a sale that is simply absent looks lost, and a
 * marketer who cannot find their own sale rings the office.
 */
function SaleTable({ sales }: { sales: CommissionSale[] }) {
  if (!sales.length) {
    return <EmptyState title="No sales have been recorded against you yet." hint="They appear here as the agency records them." />;
  }
  return (
    <div data-commission-sales>
      <Table
        head={
          <tr>
            <th>Sale</th>
            <th>Buyer</th>
            <th>Rate</th>
            <th>Earns</th>
            <th className="num">In full</th>
            <th className="num">Earned so far</th>
            <th>State</th>
          </tr>
        }
      >
        {sales.map((sale) => (
          <tr key={sale.reference}>
            <td className="font-medium">{saleWords(sale)}</td>
            {/* The buyer's name, and nothing else that belongs to them: not their address, not what
                they still owe. It is how a marketer knows which sale this is. */}
            <td>{sale.buyerName ?? "—"}</td>
            <td className="tabular">{sale.commissionBp === null ? "—" : formatRate(sale.commissionBp)}</td>
            {/* Kept on one line: "As the buyer pays" broken over three rows in a seven-column table
                reads as three separate things rather than as one phrase. */}
            <td className="whitespace-nowrap text-ink-500">{basisWords(sale.basis)}</td>
            <td className="num tabular">{sale.fullLabel}</td>
            <td className="num tabular">{sale.earned > 0 ? sale.earnedLabel : "—"}</td>
            <td>
              <SaleState status={sale.status} />
            </td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

/**
 * The lines behind the figure, so it can be checked rather than believed.
 *
 * This is the part that makes the summary arguable in the right way: each row says which sale, what
 * the share was taken of, at what rate, and on what day. A total with nothing behind it is a number
 * somebody has to accept.
 */
function LineTable({ lines }: { lines: CommissionLine[] }) {
  if (!lines.length) {
    return (
      <EmptyState
        title="Nothing has been earned yet."
        hint="A share is earned as the money it is a share of comes in."
      />
    );
  }
  return (
    <div data-commission-lines>
      <Table
        head={
          <tr>
            <th>Day</th>
            <th>Sale</th>
            <th className="num">Taken of</th>
            <th>Rate</th>
            <th className="num">Earned</th>
          </tr>
        }
      >
        {lines.map((line, index) => (
          <tr key={`${line.reference}-${line.date}-${index}`}>
            <td>{formatDate(line.date)}</td>
            <td className="font-medium">{saleWords(line)}</td>
            <td className="num tabular">{line.grossLabel}</td>
            <td className="tabular">{formatRate(line.bp)}</td>
            <td className="num tabular font-semibold">{line.commissionLabel}</td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

/** What the agency has actually handed over, by its own reference — which is what somebody quotes. */
function PaymentTable({ payments }: { payments: CommissionPayment[] }) {
  if (!payments.length) {
    return <EmptyState title="Nothing has been paid out to you yet." />;
  }
  return (
    <div data-commission-payments>
      <Table
        head={
          <tr>
            <th>Reference</th>
            <th>Paid</th>
            <th className="num">Amount</th>
          </tr>
        }
      >
        {payments.map((payment, index) => (
          <tr key={`${payment.reference ?? "payment"}-${index}`}>
            <td className="font-medium">{payment.reference ?? "—"}</td>
            <td>{formatDate(payment.paidOn)}</td>
            <td className="num tabular">{payment.amountLabel}</td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

/* --------------------------------------------------------------- loading it */

/** Load the account, or the same thing dressed as a statement. Both answers have the same shape. */
function useCommission(path: string) {
  const { get, loading, me } = usePortal();
  const [data, setData] = useState<CommissionAccount | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (loading || !me) return;
    let cancelled = false;
    get<CommissionAccount>(path)
      .then((value) => {
        if (!cancelled) setData(value);
      })
      .catch((problem: unknown) => {
        if (!cancelled) setError(problem instanceof Error ? problem.message : "That could not be opened.");
      });
    return () => {
      cancelled = true;
    };
  }, [get, path, loading, me]);

  return { data, error, loading, me };
}

/**
 * Signed out, still loading, or somebody the agency has no marketer record for — the three ways
 * this screen has nothing to draw.
 *
 * The third is the same 404 a made-up id gets anywhere else in this API, and it says nothing about
 * whether the agency has marketers at all. Drawn as a wrong turn, because for the person reading it
 * that is what it is.
 */
function CommissionGate({ error, loading, me }: { error: string | null; loading: boolean; me: PortalMe | null }) {
  if (!loading && !me) return <Navigate to="/portal/sign-in" replace />;

  if (error) {
    return (
      <PortalShell agency={me?.agency}>
        <Card>
          <h1 className="text-xl font-semibold">Not found</h1>
          <div className="mt-3">
            <Alert tone="danger">{error}</Alert>
          </div>
          <p className="mt-3 text-sm text-ink-500">
            If you sell for {me?.agency?.name ?? "this agency"} and this is not what you expected, ask them to check the
            address they hold for you.
          </p>
          <Link to="/portal" className="btn btn-secondary mt-5 w-full">
            Back to your account
          </Link>
        </Card>
      </PortalShell>
    );
  }

  return (
    <PortalShell agency={me?.agency}>
      <div className="flex items-center gap-3">
        <Spinner />
        <p className="text-sm text-ink-500">Loading…</p>
      </div>
    </PortalShell>
  );
}

/* ------------------------------------------------------------------ the screen */

export function PortalCommission() {
  const { data, error, loading, me } = useCommission("/commission");
  usePageTitle(me?.agency?.name ? `Your commission · ${me.agency.name}` : "Your commission");

  if (!data) return <CommissionGate error={error} loading={loading} me={me} />;

  const { summary } = data;

  return (
    <PortalShell agency={me?.agency}>
      <div className="mb-5">
        <Link to="/portal" className="link text-sm">
          ← Your account
        </Link>
        <h1 className="mt-2 flex items-center gap-2 text-2xl font-semibold">
          <IconPayout className="size-6 text-ink-400" />
          Your commission
        </h1>
        <p className="mt-1 text-sm text-ink-500">
          {data.marketer.name} · as at {formatDate(data.asAt)}
        </p>
      </div>

      {/*
        The figure they came for, first and largest.

        `owed` is everything earned less everything paid, cumulative and over no period at all — a
        landlord ledger takes one because a payout run draws a window, and there is no such act
        here. It is allowed to be negative, where somebody was paid ahead of a sale that was then
        cancelled, and it says so in words rather than being quietly clamped to nothing: clamping
        loses the debt the moment it appears.
      */}
      <Card className="mb-5">
        <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Owed to you</p>
        <p className={`tabular mt-1 text-3xl font-semibold ${summary.owed < 0 ? "text-danger-700" : "text-ink-900"}`}>
          {summary.owedLabel}
        </p>
        <p className="mt-1 text-sm text-ink-500">
          {summary.owed > 0
            ? "Earned and not yet paid out."
            : summary.owed < 0
              ? "You have been paid ahead of what has been earned so far."
              : "Everything earned so far has been paid out."}
        </p>

        <dl className="mt-4 grid gap-x-8 sm:grid-cols-2">
          <Detail label="Earned">{summary.earnedLabel}</Detail>
          <Detail label="Paid to you">{summary.paidLabel}</Detail>
          <Detail label="Sales">{summary.saleCount}</Detail>
          <Detail label="Entries behind it">{summary.lineCount}</Detail>
        </dl>

        <div className="mt-4">
          <Link to="/portal/commission/statement" className="btn btn-secondary btn-sm">
            <span className="inline-flex items-center gap-1.5">
              <IconStatement className="size-4" /> Your statement
            </span>
          </Link>
        </div>
      </Card>

      <Card className="mb-5" padded={false}>
        <div className="p-4 sm:p-5">
          <CardTitle hint="Every sale recorded against you, and what each has earned so far.">Your sales</CardTitle>
        </div>
        <SaleTable sales={data.sales} />
      </Card>

      <Card className="mb-5" padded={false}>
        <div className="p-4 sm:p-5">
          <CardTitle hint="A share is earned as the money it is a share of comes in.">What you have earned</CardTitle>
        </div>
        <LineTable lines={data.lines} />
      </Card>

      <Card padded={false}>
        <div className="p-4 sm:p-5">
          <CardTitle hint="A payment that was withdrawn is not listed; the money simply goes back into what is owed.">
            Paid to you
          </CardTitle>
        </div>
        <PaymentTable payments={data.payments} />
      </Card>

      {me?.agency ? (
        <p className="mt-6 text-sm text-ink-500">
          If any of this looks wrong, speak to {me.agency.name}
          {me.agency.phone ? ` on ${me.agency.phone}` : ""}
          {me.agency.email ? ` or at ${me.agency.email}` : ""}.
        </p>
      ) : null}
    </PortalShell>
  );
}

/* ------------------------------------------------------------------ the statement */

/**
 * The statement, as a marketer would print it or send it on.
 *
 * The same figures as the screen above with the agency's name and contact details on top — which is
 * what they need in order to chase a payment — laid out to be read on paper by somebody who was not
 * in the room. The buyer's statement is built exactly this way for exactly this reason: `printable`
 * puts it alone on the sheet and everything else on the page carries `no-print`.
 */
export function PortalCommissionStatement() {
  const { data, error, loading, me } = useCommission("/commission/statement");
  usePageTitle(data ? `Commission statement · ${data.marketer.name}` : "Your commission statement");

  if (!data) return <CommissionGate error={error} loading={loading} me={me} />;

  const { summary, agency } = data;

  return (
    <PortalShell agency={me?.agency}>
      <div className="no-print mb-5 flex flex-wrap items-center justify-between gap-3">
        <Link to="/portal/commission" className="link text-sm">
          ← Back to your commission
        </Link>
        <Button variant="secondary" size="sm" onClick={() => window.print()}>
          Print or save
        </Button>
      </div>

      <Card className="printable" padded={false}>
        <div className="border-b border-ink-200 p-5">
          {/* The agency's letterhead leads, exactly as on the buyer's statement beside it and on
              every document the agency's own application prints. One component, one treatment. */}
          <div className="flex flex-wrap items-start justify-between gap-4">
            {agency ? (
              <AgencyMark name={agency.name} logoUrl={agency.logoUrl} size="compact">
                {agency.phone ? <p className="text-xs text-ink-500">{agency.phone}</p> : null}
                {agency.email ? <p className="text-xs text-ink-500">{agency.email}</p> : null}
              </AgencyMark>
            ) : (
              <div />
            )}
            <div className="sm:text-right">
              <h1 className="text-xl font-semibold">Commission statement</h1>
              <p className="mt-1 text-sm text-ink-500">{data.marketer.name}</p>
              <p className="mt-0.5 text-sm text-ink-500">As at {formatDate(data.asAt)}</p>
            </div>
          </div>
        </div>

        <div className="grid gap-x-8 border-b border-ink-200 p-5 sm:grid-cols-2">
          <dl>
            <Detail label="Earned">{summary.earnedLabel}</Detail>
            <Detail label="Paid to you">{summary.paidLabel}</Detail>
          </dl>
          <dl>
            <Detail label="Owed to you">{summary.owedLabel}</Detail>
            <Detail label="Sales">{summary.saleCount}</Detail>
          </dl>
        </div>

        <div className="p-5 pb-2">
          <CardTitle>Your sales</CardTitle>
        </div>
        <SaleTable sales={data.sales} />

        <div className="p-5 pb-2">
          <CardTitle>What you have earned</CardTitle>
        </div>
        <LineTable lines={data.lines} />

        <div className="p-5 pb-2">
          <CardTitle>Paid to you</CardTitle>
        </div>
        <PaymentTable payments={data.payments} />

        {agency ? (
          <p className="p-5 text-sm text-ink-500">
            Anything that looks wrong, speak to {agency.name}
            {agency.phone ? ` on ${agency.phone}` : ""}
            {agency.email ? ` or at ${agency.email}` : ""}.
          </p>
        ) : null}
      </Card>
    </PortalShell>
  );
}

/* --------------------------------------------------------------------- the routes */

export function PortalCommissionPage() {
  return (
    <PortalProvider>
      <PortalCommission />
    </PortalProvider>
  );
}

export function PortalCommissionStatementPage() {
  return (
    <PortalProvider>
      <PortalCommissionStatement />
    </PortalProvider>
  );
}
